import { diffWordsWithSpace } from 'diff';

/**
 * FRONT-2669 — the Changes view's text, as segments: unchanged, removed (the old
 * words) and added (the new words). A WORD diff that keeps whitespace as its own
 * tokens, so a reflowed paragraph shows only the words that really changed and
 * line breaks survive for `whitespace-pre-wrap`. Pure — no DOM, no diff types leak.
 */
export type ChangeSegmentKind = 'same' | 'removed' | 'added';
export interface ChangeSegment {
  kind: ChangeSegmentKind;
  text: string;
}

/** Wall-clock budget for one diff. A full rewrite of a long document is the worst case for the algorithm. */
export const DIFF_TIMEOUT_MS = 1000;
// No edit-length cap: an append of thousands of words is a LONG edit script yet cheap to find, and a cap
// turned it into the fallback. The time budget alone bounds the unrelated-texts worst case.

/**
 * The segments, or null when the diff gave up inside its budget (`timeout`;
 * jsdiff answers undefined then). The caller shows a sentence
 * instead of freezing the tab on an unrelated pair of long texts.
 */
export function changeSegments(before: string, after: string): ChangeSegment[] | null {
  const parts = diffWordsWithSpace(before, after, { timeout: DIFF_TIMEOUT_MS });
  if (!parts) return null;
  return parts
    .filter((part) => part.value.length > 0)
    .map((part) => ({
      kind: part.added ? 'added' : part.removed ? 'removed' : 'same',
      text: part.value,
    }));
}
